import {
  ApplicationConfig,
  inject,
  isDevMode,
  LOCALE_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withViewTransitions,
} from '@angular/router';
import {
  HttpClient,
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import { mobileClientInterceptor } from './common/interceptors/mobile-client.interceptor';
import { mobileCabinetPlatformProviders } from './common/adapters/mobile-cabinet-platform.providers';
import { provideStaffClientQrPrint } from '@nebalkon-workspace/cabinet-staff';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import {
  httpLoaderFactory,
  HTTP_LOADER_CONFIG,
} from '@nebalkon-workspace/core';
import {
  AddressService,
  AuthService,
  authInterceptor,
  extractRolesFromToken,
  ProfileService,
  PromocodeApiService,
} from '@nebalkon-workspace/auth';
import {
  BoxService,
  BoxSizeService,
  ItemService,
  WarehouseService,
} from '@nebalkon-workspace/storage-api';
import { PhotoUploadService } from '@nebalkon-workspace/media-api';
import {
  DashboardAdminService,
  EmployeeAdminService,
  MediaAdminService,
  UserAdminService,
  WalletAdminService,
} from '@nebalkon-workspace/admin-api';
import { provideNebalkonEcharts } from '@nebalkon-workspace/cabinet-staff';
import { OrderApiService } from '@nebalkon-workspace/order-api';
import { AdminLeadApiService } from '@nebalkon-workspace/lead-api';
import { claimGuestOrdersAfterProfileLoaded } from '@nebalkon-workspace/cabinet-client';
import { catchError, firstValueFrom, Observable, of, switchMap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { provideYandexMetrika } from '@grandgular/yandex-metrika';
import { DIALOG_SCROLL_STRATEGY } from '@angular/cdk/dialog';
import { Overlay } from '@angular/cdk/overlay';
import { appRoutes } from './app.routes';
import { environment } from '@nebalkon-workspace/environments';
import { CalculatorPricingConfigService } from '@nebalkon-workspace/cabinet-core';

/**
 * Initialize app with auth and user profile
 * Navigation logic is handled by guards (including initialRedirectGuard)
 */
export function initializeAppFactory(): Observable<unknown> | void {
  const authService = inject(AuthService);
  const profileService = inject(ProfileService);
  const addressService = inject(AddressService);
  const boxService = inject(BoxService);
  const boxSizeService = inject(BoxSizeService);
  const itemService = inject(ItemService);
  const warehouseService = inject(WarehouseService);
  const employeeAdminService = inject(EmployeeAdminService);
  const userAdminService = inject(UserAdminService);
  const dashboardAdminService = inject(DashboardAdminService);
  const mediaAdminService = inject(MediaAdminService);
  const walletAdminService = inject(WalletAdminService);
  const photoUploadService = inject(PhotoUploadService);
  const orderApi = inject(OrderApiService);
  const adminLeadApi = inject(AdminLeadApiService);
  const promocodeApi = inject(PromocodeApiService);

  const apiUrl = `${environment.apiUrl}/api/v1`;

  authService.configure({ apiUrl, roleDecoder: extractRolesFromToken });
  authService.checkAuthStatus();

  profileService.configure({ apiUrl });
  promocodeApi.configure({ apiUrl });
  addressService.configure({ apiUrl });
  boxService.configure({ apiUrl });
  boxSizeService.configure({ apiUrl });
  itemService.configure({ apiUrl });
  warehouseService.configure({ apiUrl });
  employeeAdminService.configure({ apiUrl });
  userAdminService.configure({ apiUrl });
  dashboardAdminService.configure({ apiUrl });
  mediaAdminService.configure({ apiUrl });
  walletAdminService.configure({ apiUrl });
  photoUploadService.configure({ apiUrl });
  orderApi.configure({ apiUrl });
  adminLeadApi.configure({ apiUrl });

  if (authService.isAuthenticated()) {
    return profileService.loadProfile().pipe(
      switchMap(() =>
        claimGuestOrdersAfterProfileLoaded(profileService, orderApi).pipe(
          catchError((err) => {
            console.error('Failed to claim orders on app init', err);
            return of(undefined);
          }),
        ),
      ),
      catchError((error) => {
        console.error('Failed to load profile on app init', error);
        return of(null);
      }),
    );
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    ...mobileCabinetPlatformProviders,
    provideStaffClientQrPrint(),
    { provide: LOCALE_ID, useValue: 'ru' },
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(
      appRoutes,
      withComponentInputBinding(),
      withViewTransitions(),
    ),
    provideHttpClient(
      withFetch(),
      withInterceptors([mobileClientInterceptor, authInterceptor]),
    ),
    provideEnvironmentNgxMask(),
    provideNebalkonEcharts(),
    {
      provide: HTTP_LOADER_CONFIG,
      useValue: {
        prefix: '/i18n/',
        suffix: '.json',
        resources: [
          { prefix: '/i18n/', suffix: '.json' },
          { prefix: '/i18n/cabinet-client/', suffix: '.json' },
          { prefix: '/i18n/cabinet-staff/', suffix: '.json' },
          { prefix: '/i18n/ui-kit/', suffix: '.json' },
        ],
      },
    },
    provideTranslateService({
      fallbackLang: 'ru',
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    provideAppInitializer(() =>
      firstValueFrom(inject(TranslateService).use('ru')),
    ),
    provideAppInitializer(() =>
      inject(CalculatorPricingConfigService).load(),
    ),
    // Initialize app
    provideAppInitializer(initializeAppFactory),
    {
      provide: DIALOG_SCROLL_STRATEGY,
      deps: [Overlay],
      useFactory: (overlay: Overlay) => () => overlay.scrollStrategies.noop(),
    },
    provideYandexMetrika([
      {
        id: 106839682, //nebalkon.by
        prodOnly: true,
        options: {
          clickmap: true,
          trackLinks: true,
          accurateTrackBounce: true,
          webvisor: true,
          ecommerce: 'dataLayer',
        },
      },
      ...(isDevMode() //todo. fix
        ? [
            {
              id: 105971976,
              prodOnly: false,
              options: {
                clickmap: true,
                trackLinks: true,
                accurateTrackBounce: true,
                webvisor: true,
                ecommerce: 'dataLayer',
              },
            },
          ]
        : []),
    ]),
  ],
};
